import { formatCurrencyMajorUnit } from '../../util/currency';
import { parseDateFromISO8601 } from '../../util/dates';

// Task card (D11) data helpers, shared by ListingCard and SearchMapInfoCard.
// Pure functions: display-only formatting, stored listing data is never changed.

const DEADLINE_ISO_DATE_REGEXP = /^\d{4}-\d{2}-\d{2}$/;
const NUMERIC_SEGMENT_REGEXP = /^[\d\s-]+$/;
// "75001 Paris", "00-950 Warszawa", "123 45 Stockholm" → postcode + place name.
// A 1–3 digit prefix is a house number ("12 Rue de Rivoli"), not a postcode.
const POSTCODE_PLACE_REGEXP = /^(?:\d{4,6}|\d{2}-\d{3}|\d{3} \d{2})\s+(\D.*)$/;

const isValidBudgetSide = value => Number.isInteger(value) && value >= 0;

/**
 * Budget value for the task card, without the label (§9): «56–89 €», «от 50 €», «до 85 €».
 *
 * @param {Object} publicData listing publicData (budgetMin / budgetMax, integer major units)
 * @param {Object} intl react-intl
 * @param {string} currency marketplace currency
 * @returns {string|null} formatted value, or null when the task has no budget
 */
export const getTaskBudgetValue = (publicData, intl, currency) => {
  const min = publicData?.budgetMin;
  const max = publicData?.budgetMax;
  const hasMin = isValidBudgetSide(min);
  const hasMax = isValidBudgetSide(max);
  const format = value => formatCurrencyMajorUnit(intl, currency, value);

  if (hasMin && hasMax) {
    // §9 range: the currency once, after the upper bound («56–89 €»)
    return intl.formatMessage(
      { id: 'ListingCard.budgetRangeValue' },
      { min: intl.formatNumber(min, { useGrouping: true }), max: format(max) }
    );
  }
  if (hasMin) {
    return intl.formatMessage({ id: 'ListingCard.budgetFromValue' }, { min: format(min) });
  }
  if (hasMax) {
    return intl.formatMessage({ id: 'ListingCard.budgetToValue' }, { max: format(max) });
  }
  return null;
};

/**
 * Deadline text: «до 15 августа» (the year is added only when it is not the current year),
 * or «Без срока» when the task has no valid deadline.
 *
 * @param {Object} publicData listing publicData (deadline as YYYY-MM-DD)
 * @param {Object} intl react-intl
 * @param {Date} [now] current date (for tests)
 * @returns {{ hasDeadline: boolean, text: string }}
 */
export const getTaskDeadline = (publicData, intl, now = new Date()) => {
  const raw = publicData?.deadline;
  if (typeof raw !== 'string' || !DEADLINE_ISO_DATE_REGEXP.test(raw)) {
    return { hasDeadline: false, text: intl.formatMessage({ id: 'ListingCard.noDeadline' }) };
  }
  const date = parseDateFromISO8601(raw);
  const sameYear = date.getFullYear() === now.getFullYear();
  const formatted = intl.formatDate(date, {
    day: 'numeric',
    month: 'long',
    ...(sameYear ? {} : { year: 'numeric' }),
  });
  return {
    hasDeadline: true,
    text: intl.formatMessage({ id: 'ListingCard.deadlineBy' }, { date: formatted }),
  };
};

// Postcode forms: "75012", "9630000", "00-950", "123 45".
const POSTCODE = '(?:\\d{4,7}|\\d{2}-\\d{3}|\\d{3} \\d{2})';
const LEADING_POSTCODE_REGEXP = new RegExp(`^${POSTCODE}\\s+`);
const TRAILING_POSTCODE_REGEXP = new RegExp(`\\s+${POSTCODE}$`);
// A house number token: "12", "12a", "5/2", "14-16".
const HOUSE_NUMBER_REGEXP = /^\d{1,4}[a-zа-яё]?(?:[/-]\d{1,4}[a-zа-яё]?)?$/i;
// Full street words, matched as whole tokens only (never inside a word: «Viareggio», «Rueil»).
// Abbreviations (st, rd, ave, ул, пр-т …) are deliberately not used on their own: «St. Petersburg»
// is a place; a segment with a house number is treated as a street anyway.
const STREET_WORDS = new Set([
  'street',
  'road',
  'avenue',
  'boulevard',
  'lane',
  'drive',
  'rue',
  'allée',
  'allee',
  'chemin',
  'quai',
  'impasse',
  'straße',
  'strasse',
  'gasse',
  'weg',
  'platz',
  'calle',
  'avenida',
  'via',
  'viale',
  'piazza',
  'rua',
  'ulica',
  'улица',
  'проспект',
  'переулок',
  'шоссе',
  'бульвар',
  'набережная',
  'площадь',
  'вулиця',
]);

const isStreetSegment = segment => {
  const tokens = segment
    .split(/\s+/)
    .map(t => t.replace(/[.,;:]+$/, '').toLowerCase())
    .filter(Boolean);
  return tokens.some(t => HOUSE_NUMBER_REGEXP.test(t) || STREET_WORDS.has(t));
};

const stripPostcode = segment =>
  segment
    .replace(LEADING_POSTCODE_REGEXP, '')
    .replace(TRAILING_POSTCODE_REGEXP, '')
    .trim();

/**
 * Short location «Город, страна» from the free-text geocoded address (Mapbox place_name; tasks
 * store no structured city). Display only:
 * - country = the last segment, without a postcode;
 * - city = the place after a postcode («75001 Paris»); otherwise the first named segment after
 *   dropping postcodes, numeric segments and street segments (a house number or a full street word),
 *   i.e. the most specific place, never a district or region listed after it.
 * Safe fallback: no city → the country alone; nothing usable → '' (the row is hidden).
 * The raw address is never returned, so a street or house number never reaches a public card.
 *
 * @param {string} address e.g. "75012, Париж, Иль-де-Франс, Франция"
 * @returns {string} e.g. "Париж, Франция"
 */
export const getShortLocation = address => {
  if (typeof address !== 'string') {
    return '';
  }
  const segments = address
    .replace(/\s+/g, ' ')
    .split(',')
    .map(s => s.trim())
    .filter(Boolean);
  if (segments.length === 0) {
    return '';
  }

  const isPlaceName = s => s && !NUMERIC_SEGMENT_REGEXP.test(s) && !isStreetSegment(s);

  if (segments.length === 1) {
    const only = stripPostcode(segments[0]);
    return isPlaceName(only) ? only : '';
  }

  const countryRaw = stripPostcode(segments[segments.length - 1]);
  const country = countryRaw && !NUMERIC_SEGMENT_REGEXP.test(countryRaw) ? countryRaw : '';
  const middle = segments.slice(0, -1);

  const withPostcode = middle
    .map(s => s.match(POSTCODE_PLACE_REGEXP))
    .filter(Boolean)
    .pop();
  const postcodeCity = withPostcode ? withPostcode[1].trim() : '';

  const named = middle
    .map(stripPostcode)
    .filter(isPlaceName)
    .filter((s, i, list) => i === 0 || s.toLowerCase() !== list[i - 1].toLowerCase());
  const city = postcodeCity || named[0] || '';

  if (city && country) {
    return `${city}, ${country}`;
  }
  return city || country;
};

/**
 * Task format from fulfillmentModes using the hosted option labels («Онлайн», «Офлайн»).
 *
 * @param {Object} publicData listing publicData
 * @param {Array<Object>} listingFields hosted listing field config
 * @returns {string|null} e.g. "Онлайн · Офлайн", or null
 */
export const getTaskFormat = (publicData, listingFields) => {
  const modes = publicData?.fulfillmentModes;
  if (!Array.isArray(modes) || modes.length === 0 || !Array.isArray(listingFields)) {
    return null;
  }
  const options = listingFields.find(f => f?.key === 'fulfillmentModes')?.enumOptions || [];
  const labels = options
    .filter(o => modes.includes(o?.option))
    .map(o => (typeof o.label === 'string' ? o.label.trim() : ''))
    .filter(Boolean);
  return labels.length > 0 ? labels.join(' · ') : null;
};

/**
 * Level-1 category of a listing: id and hosted display name.
 *
 * @param {Object} publicData listing publicData (categoryLevel1)
 * @param {Object} categoryConfiguration config.categoryConfiguration
 * @returns {{ id: string, name: string }|null}
 */
export const getTaskCategory = (publicData, categoryConfiguration) => {
  const id = publicData?.categoryLevel1;
  if (typeof id !== 'string' || !id) {
    return null;
  }
  const categories = categoryConfiguration?.categories || [];
  const found = categories.find(c => c?.id === id);
  return { id, name: found?.name || '' };
};
